import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut as fbSignOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { auth } from './firebase';

const EMAIL_STORAGE_KEY = 'cmp_signin_email';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  sendLink: (email: string) => Promise<void>;
  completeSignIn: () => Promise<{ ok: true; user: User } | { ok: false; error: string }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const sendLink = async (email: string) => {
    const continueUrl = `${window.location.origin}/auth/callback`;
    await sendSignInLinkToEmail(auth, email, {
      url: continueUrl,
      handleCodeInApp: true,
    });
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  };

  const completeSignIn = async (): Promise<
    { ok: true; user: User } | { ok: false; error: string }
  > => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return { ok: false, error: 'No sign-in link in URL.' };
    }
    let email = window.localStorage.getItem(EMAIL_STORAGE_KEY);
    if (!email) {
      email = window.prompt('Confirm your email to finish sign-in') ?? '';
      if (!email) return { ok: false, error: 'Email is required to finish sign-in.' };
    }
    try {
      const cred = await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      return { ok: true, user: cred.user };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : 'Sign-in failed',
      };
    }
  };

  const signOut = async () => {
    await fbSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, sendLink, completeSignIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
